import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { submitNewsletterSignup } from '../api/newsletter';
import { syncUserProfileDebounced } from '../api/users';
import { trackNewsletterSignupAttempt } from '../analytics';
import { setCategoryConsent } from '../utils/consentPreferences';
import { useGettoncini } from '../contexts/GettonciniContext';
import { useAlertQueueActions } from '../contexts/AlertQueueContext';
import {
  resolveNewsletterSignupAlert,
  NEWSLETTER_TRANSACTION_KEY,
} from '../utils/newsletterGettoncini';
import { queueResourceAlertsFromPayload } from '../utils/resourceAlerts';
import { useUser, useAuth } from '@clerk/clerk-react';
import styles from '../styles/NewsletterSignupModal.module.css';
import FaIcon from './FaIcon';
import GettonciniCostChip from './GettonciniCostChip';

const NewsletterSignupContent = ({ showDismiss = false, onDismiss, onSuccess, onReturnHome }) => {
  const { isSignedIn, user } = useUser();
  const { getToken } = useAuth();
  
  // Get initial email from Clerk (if signed in) or localStorage
  const getInitialEmail = () => {
    try {
      if (isSignedIn && user) {
        if (user.primaryEmailAddress?.emailAddress) return user.primaryEmailAddress.emailAddress;
        const first = user.emailAddresses?.[0]?.emailAddress;
        if (first) return first;
      }
    } catch (_) {}
    return localStorage.getItem('email') || '';
  };

  const [email, setEmail] = useState(getInitialEmail);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // 'success', 'error', null
  const { updateBalance, transactionTypes } = useGettoncini();
  const newsletterRewardAmount = (() => {
    const configuredAmount = Number(transactionTypes?.[NEWSLETTER_TRANSACTION_KEY]?.amount);
    if (Number.isFinite(configuredAmount) && configuredAmount > 0) {
      return Math.trunc(configuredAmount);
    }
    return null;
  })();
  // Only get actions, not state - prevents unnecessary re-renders
  const { queueAlert } = useAlertQueueActions();

  // Update email when user signs in
  useEffect(() => {
    const newEmail = getInitialEmail();
    if (newEmail && newEmail !== email) {
      setEmail(newEmail);
    }
  }, [isSignedIn, user]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!email.trim()) {
      setSubmitStatus('error');
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const userId = localStorage.getItem('user-id');
      if (!userId) {
        throw new Error('No user ID found');
      }

      const token = isSignedIn
        ? await getToken({ template: 'basic_user_info_jwt_template' }).catch(() => null)
        : null;

      const result = await submitNewsletterSignup(email.trim(), token, 'newsletter_modal');

      if (result.success) {
        if (email.trim() !== localStorage.getItem('email')) {
          localStorage.setItem('email', email.trim());
        }

        setCategoryConsent({ marketing: true, source: 'settings' });
        localStorage.setItem('newsletter-signup-completed', Date.now().toString());

        syncUserProfileDebounced();

        const alertPayload = resolveNewsletterSignupAlert({
          result,
          isSignedIn,
          transactionTypes,
        });
        if (alertPayload) {
          const balance = alertPayload?.payload?.gettoncini?.balance;
          if (typeof balance === 'number') {
            updateBalance(balance);
          }
          queueResourceAlertsFromPayload({
            queueAlert,
            payload: alertPayload.payload,
            isAnonymous: alertPayload.isAnonymous,
            firstAlertDelayMs: 1000,
            signedInDurationMs: 8000,
            anonymousDurationMs: 8000,
          });
        }

        trackNewsletterSignupAttempt(true);

        setSubmitStatus('success');

        if (onSuccess) {
          setTimeout(() => {
            onSuccess();
          }, 1500);
        }
      } else {
        trackNewsletterSignupAttempt(false);
        setSubmitStatus('error');
      }
    } catch (error) {
      console.error('Newsletter signup error:', error);
      trackNewsletterSignupAttempt(false);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEmailChange = (e) => {
    setEmail(e.target.value);
    setSubmitStatus(null);
  };

  return (
    <div className={styles.modalContent}>
      {/* Close button */}
      <button
        type="button"
        onClick={onDismiss}
        className={styles.closeButton}
        aria-label="Chiudi"
      >
        <FaIcon className="fas fa-times"  />
      </button>

      {/* Background decorations - simplified for modal */}
      <div className={styles.backgroundDecorations}>
        <FaIcon className={`fas fa-envelope ${styles.floatingShape} ${styles.shape1}`}  />
        <FaIcon className={`fas fa-paper-plane ${styles.floatingShape} ${styles.shape2}`}  />
        <FaIcon className={`fas fa-envelope-open-text ${styles.floatingShape} ${styles.shape3}`}  />
      </div>
      
      <div className={styles.sparkles}>
        <div className={styles.sparkle} />
        <div className={styles.sparkle} />
        <div className={styles.sparkle} />
        <div className={styles.sparkle} />
      </div>

      {/* Main content */}
      <div className={styles.content}>
        {/* Icon */}
        <div className={styles.iconContainer}>
          <div className={styles.newsletterIcon}>
            <FaIcon className="fas fa-envelope-open-text"  />
          </div>
        </div>

        {/* Title */}
        <h2 className={styles.title}>
          Non perderti niente!
        </h2>
        
        {/* Subtitle */}
        <p className={styles.subtitle}>
          Iscriviti alla newsletter per scoprire i giochini extra e ricevere aggiornamenti in anteprima e offerte speciali riservate alla nostra community.
        </p>

        {submitStatus === 'success' ? (
          <div className={styles.successContainer}>
            <div className={styles.successBadge}>
              <FaIcon className={`fas fa-check-circle ${styles.successIcon}`}  />
              <div className={styles.successText}>
                <span className={styles.successLabel}>Perfetto! Ti terremo aggiornato!</span>
              </div>
            </div>
            {onReturnHome && (
              <button
                type="button"
                className={styles.homeButton}
                onClick={onReturnHome}
              >
                <FaIcon className="fas fa-gamepad"  />
                <span>Torna ai giochi</span>
              </button>
            )}
          </div>
        ) : (
          <>
            {/* Form */}
            <form onSubmit={handleSubmit} className={styles.form}>
              <div className={styles.inputGroup}>
                <input
                  id="newsletter-email"
                  type="email"
                  value={email}
                  onChange={handleEmailChange}
                  placeholder="La tua email"
                  className={styles.emailInput}
                  disabled={isSubmitting}
                  required
                />
                <div className={styles.submitButtonWrap}>
                  <button
                    type="submit"
                    className={`${styles.submitButton} ${isSubmitting ? styles.submitting : ''}`}
                    disabled={isSubmitting || !email.trim()}
                  >
                    {isSubmitting ? 'Iscrizione...' : 'Iscriviti gratis'}
                  </button>
                  <GettonciniCostChip
                    amount={newsletterRewardAmount}
                    direction="earn"
                    compact
                    className={styles.submitRewardChip}
                  />
                </div>
              </div>
              
              {submitStatus === 'error' && (
                <div className={styles.errorMessage}>
                  Si è verificato un errore. Riprova più tardi.
                </div>
              )}
              
              <p className={styles.privacyNote}>
                Puoi disiscriverti in qualsiasi momento. Niente spam, promesso!
              </p>
            </form>

            {/* Learn more link */}
            <Link to="/newsletter" className={styles.learnMoreLink} onClick={onDismiss}>
              Scopri di più sulla newsletter
              <FaIcon className="fas fa-arrow-right"  />
            </Link>


          </>
        )}
      </div>
    </div>
  );
};

export default NewsletterSignupContent;
